import React, { useMemo, useState, useEffect } from 'react';
import AdminNav from '../../components/admin/AdminNav';
import TeamHierarchyModal from '../../components/TeamHierarchyModal';
import CustomDropdown from '../../components/CustomDropdown';
import { getRoleLabel, ROLE_OPTIONS } from '../../utils/constants';

const RegionalOfficersManagement = ({ backendUrl }) => {
  const [regionalOfficers, setRegionalOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [usersStats, setUsersStats] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [teamModalOfficer, setTeamModalOfficer] = useState(null);
  const [teamSearch, setTeamSearch] = useState('');
  const [assigningEmail, setAssigningEmail] = useState(null);
  const [assignDropdownEmail, setAssignDropdownEmail] = useState(null); // Which user's "Add" dropdown is open

  // Form state for adding new officer
  const [showAddForm, setShowAddForm] = useState(false);
  const [newOfficer, setNewOfficer] = useState({
    name: '',
    email: '',
    phone: '',
    region: '',
    branches: [],
    states: [],
    role: 'regional_sales_leader', // 'regional_sales_leader' or 'area_sales_manager'
    reports_to: null // RSL ID if role is area_sales_manager
  });

  const [editingOfficer, setEditingOfficer] = useState(null);
  const [sortBy, setSortBy] = useState('name'); // name, region, states
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  // Default to collapsed on fresh page load, but preserve state for in-app navigation
  const [showOfficersList, setShowOfficersList] = useState(() => {
    const stored = sessionStorage.getItem('regionalOfficersListExpanded');
    return stored !== null ? stored === 'true' : false; // Default to collapsed (false) on fresh load
  });
  const [expandedRSLs, setExpandedRSLs] = useState({}); // Track which RSLs are expanded

  // 🌳 Team Hierarchy Modal state
  const [showTeamHierarchyModal, setShowTeamHierarchyModal] = useState(false);
  const [hierarchyOfficer, setHierarchyOfficer] = useState(null);

  // Track AdminNav mode
  const [navMode, setNavMode] = useState(() => {
    return localStorage.getItem('adminNavMode') || (window.innerWidth < 1024 ? 'peek' : 'expanded');
  });

  // Listen for AdminNav toggle events
  useEffect(() => {
    const handleNavToggle = (e) => {
      if (e.detail.mode) {
        setNavMode(e.detail.mode);
      } else {
        setNavMode(e.detail.isCollapsed ? 'collapsed' : 'expanded');
      }
    };
    window.addEventListener('adminNavToggle', handleNavToggle);
    return () => window.removeEventListener('adminNavToggle', handleNavToggle);
  }, []);

  useEffect(() => {
    loadRegionalOfficers();
    loadUsersStats(); // Load users on mount to calculate team counts
  }, [backendUrl]);

  // Persist officers list expanded/collapsed state for in-app navigation
  useEffect(() => {
    sessionStorage.setItem('regionalOfficersListExpanded', showOfficersList.toString());
  }, [showOfficersList]);

  const loadRegionalOfficers = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/regional-officers`, {
        headers: { 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();

      if (data.success) {
        setRegionalOfficers(data.officers || []);
      } else {
        throw new Error(data.error || 'Failed to load officers');
      }
    } catch (err) {
      console.error('Load error:', err);
      setError(err.message);
      // Set default placeholder for testing
      setRegionalOfficers([
        {
          id: 'default-1',
          name: 'Matt Hales (Placeholder)',
          email: 'matt.hales@pyebarkerfs.com',
          region: 'All Regions',
          branches: ['All'],
          states: ['All'],
          is_default: true
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadUsersStats = async () => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/admin/users/stats`, {
        headers: { 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await res.json();
      setUsersStats(data?.users || []);
    } catch (e) {
      console.error('Failed to load users stats:', e);
      setUsersStats([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const assignUserToLeader = async (userEmail, officer) => {
    const email = (userEmail || '').trim().toLowerCase();
    if (!email || !email.includes('@')) return;

    setAssigningEmail(email);
    try {
      const officerId = officer ? (officer.id || officer._id) : null;
      const officerRole = officer?.role || 'regional_sales_leader';

      // For ASM assignments: always set RSL to the ASM's reports_to, and ASM to the officer.
      // For RSL assignments: set RSL to officer, clear ASM.
      let salesLeaderId = null;
      let salesLeaderName = '';
      let salesLeaderEmail = '';
      let asmId = null;
      let asmName = '';
      let asmEmail = '';

      if (!officer) {
        // Unassign everything
        salesLeaderId = null;
      } else if (officerRole === 'area_sales_manager') {
        const parentRslId = officer.reports_to || null;
        if (!parentRslId) {
          throw new Error('This RD is missing Reports To (RVP). Please edit the RD and set a Regional Vice President.');
        }
        const parentRsl = (regionalOfficers || []).find(o => (o.id || o._id) === parentRslId) || null;
        salesLeaderId = parentRsl ? (parentRsl.id || parentRsl._id) : parentRslId;
        salesLeaderName = parentRsl?.name || '';
        salesLeaderEmail = parentRsl?.email || '';
        asmId = officerId;
        asmName = officer.name || '';
        asmEmail = officer.email || '';
      } else {
        salesLeaderId = officerId;
        salesLeaderName = officer.name || '';
        salesLeaderEmail = officer.email || '';
        asmId = null;
        asmName = '';
        asmEmail = '';
      }

      const token = localStorage.getItem('token');
      // Base64 encode email to bypass WAF
      const encodedEmail = btoa(email);
      const res = await fetch(`${backendUrl}/api/admin/users/${encodedEmail}/sales-leader`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          sales_leader_id: salesLeaderId,
          sales_leader_name: salesLeaderName,
          sales_leader_email: salesLeaderEmail,
          area_sales_manager_id: asmId,
          area_sales_manager_name: asmName,
          area_sales_manager_email: asmEmail
        })
      });
      const j = await res.json();
      if (!res.ok || !j?.success) throw new Error(j?.detail || 'Failed to save assignment');

      // Update local users list (keep UI snappy)
      setUsersStats(prev => prev.map(u => {
        const uEmail = (u.email || '').toLowerCase();
        if (uEmail !== email) return u;
        return {
          ...u,
          regional_sales_leader_id: j.regional_sales_leader_id,
          regional_sales_leader_name: j.regional_sales_leader_name,
          regional_sales_leader_email: j.regional_sales_leader_email,
          regional_sales_leader_assigned_at: j.regional_sales_leader_assigned_at,
          area_sales_manager_id: j.area_sales_manager_id || null,
          area_sales_manager_name: j.area_sales_manager_name || '',
          area_sales_manager_email: j.area_sales_manager_email || ''
        };
      }));
    } catch (e) {
      console.error('Assign failed:', e);
      alert(`❌ Failed to update team: ${e.message}`);
    } finally {
      setAssigningEmail(null);
    }
  };

  const openTeamModal = async (officer) => {
    setTeamModalOfficer(officer);
    setTeamSearch('');
    if (!usersStats || usersStats.length === 0) {
      await loadUsersStats();
    }
  };

  const closeTeamModal = () => {
    setTeamModalOfficer(null);
    setTeamSearch('');
    setAssigningEmail(null);
    setAssignDropdownEmail(null);
  };

  const teamStats = useMemo(() => {
    const byLeader = {};
    (usersStats || []).forEach(u => {
      const lid = u.regional_sales_leader_id || null;
      if (!lid) return;
      byLeader[lid] = (byLeader[lid] || 0) + 1;
    });
    return byLeader;
  }, [usersStats]);

  const modalLists = useMemo(() => {
    const officer = teamModalOfficer;
    if (!officer) return { assigned: [], unassigned: [], hierarchy: [], asmsUnderRSL: [] };
    const lid = officer.id || officer._id;
    const q = (teamSearch || '').trim().toLowerCase();

    const withEmail = (usersStats || []).filter(u => (u.email || '').includes('@'));
    const role = officer.role || 'regional_sales_leader';

    const officerEmails = new Set(
      (regionalOfficers || []).map(o => (o.email || '').toLowerCase()).filter(Boolean)
    );
    const isOfficer = (u) => officerEmails.has((u.email || '').toLowerCase());

    const filterFn = (u) => {
      if (!q) return true;
      return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
    };

    // For RSL: Build hierarchical view
    if (role === 'regional_sales_leader') {
      // Get ASMs that report to this RSL
      const asmsUnderRSL = (regionalOfficers || []).filter(o => 
        o.role === 'area_sales_manager' && o.reports_to === lid
      );
      
      // Get ASM emails for filtering
      const asmEmails = new Set(asmsUnderRSL.map(a => (a.email || '').toLowerCase()));
      
      // All users under this RSL (excluding the RSL themselves and ASMs)
      const rslEmail = (officer.email || '').toLowerCase();
      const allUsersUnderRSL = withEmail.filter(u => {
        const userEmail = (u.email || '').toLowerCase();
        return u.regional_sales_leader_id === lid 
          && userEmail !== rslEmail  // Exclude RSL from their own team
          && !asmEmails.has(userEmail);  // Exclude ASMs (shown separately)
      });
      
      // Build hierarchy: RSL -> ASMs (with their users) -> Direct users
      const hierarchy = [];
      
      // 1. Add ASMs with their users nested
      asmsUnderRSL.forEach(asm => {
        const asmId = asm.id || asm._id;
        const usersUnderASM = allUsersUnderRSL.filter(u => u.area_sales_manager_id === asmId);
        
        // Only include if ASM or its users match search
        const asmMatchesSearch = filterFn({ name: asm.name, email: asm.email });
        const usersMatch = usersUnderASM.filter(filterFn);
        
        if (asmMatchesSearch || usersMatch.length > 0) {
          hierarchy.push({
            type: 'asm',
            officer: asm,
            users: usersMatch,
            totalPoints: usersMatch.reduce((sum, u) => sum + (u.total_points || 0), 0)
          });
        }
      });
      
      // 2. Add direct users (under RSL, no ASM) 
      const directUsers = allUsersUnderRSL.filter(u => !u.area_sales_manager_id).filter(filterFn);
      directUsers.forEach(u => {
        hierarchy.push({ type: 'user', user: u });
      });

      const unassigned = withEmail.filter(u => !u.regional_sales_leader_id && !isOfficer(u)).filter(filterFn);

      return {
        assigned: allUsersUnderRSL.filter(filterFn),
        unassigned,
        hierarchy,
        asmsUnderRSL
      };
    }
    
    // For ASM: Show users directly under this ASM
    const assignedRaw = withEmail.filter(u => u.area_sales_manager_id === lid);
    const parentRslId = officer.reports_to || null;
    const availableUnderParent = parentRslId
      ? withEmail.filter(u => u.regional_sales_leader_id === parentRslId && !u.area_sales_manager_id)
      : [];
    const fullyUnassigned = withEmail.filter(u => !u.regional_sales_leader_id && !isOfficer(u));
    const unassignedRaw = [...availableUnderParent, ...fullyUnassigned]
      .filter(u => !isOfficer(u))
      .filter((u, idx, arr) => arr.findIndex(x => x.email === u.email) === idx);

    return {
      assigned: assignedRaw.filter(filterFn),
      unassigned: unassignedRaw.filter(filterFn),
      hierarchy: assignedRaw.filter(filterFn).map(u => ({ type: 'user', user: u })),
      asmsUnderRSL: []
    };
  }, [teamModalOfficer, usersStats, teamSearch, regionalOfficers]);

  const handleAddOfficer = async () => {
    if (!newOfficer.name || !newOfficer.email) {
      alert('Name and email are required');
      return;
    }

    setSaving(true);

    try {
      const url = editingOfficer
        ? `${backendUrl}/api/admin/regional-officers/${editingOfficer.id}`
        : `${backendUrl}/api/admin/regional-officers`;

      const method = editingOfficer ? 'PUT' : 'POST';
      const token = localStorage.getItem('token');

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(newOfficer)
      });

      const data = await response.json();

      if (data.success) {
        const roleLabel = getRoleLabel(newOfficer.role);
        alert(editingOfficer ? `✅ ${roleLabel} updated!` : `✅ ${roleLabel} added!`);
        setShowAddForm(false);
        setEditingOfficer(null);
        setNewOfficer({ name: '', email: '', phone: '', region: '', branches: [], states: [], role: 'regional_sales_leader', reports_to: null });
        loadRegionalOfficers();
      } else {
        throw new Error(data.error || 'Failed to save officer');
      }
    } catch (err) {
      alert(`❌ Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOfficer = async (officerId) => {
    if (!window.confirm('Remove this officer?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/regional-officers/${officerId}`, {
        method: 'DELETE',
        headers: { 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ Officer removed');
        loadRegionalOfficers();
      } else {
        throw new Error(data.error || 'Failed to delete officer');
      }
    } catch (err) {
      alert(`❌ Error: ${err.message}`);
    }
  };

  const handleImportCSV = async () => {
    if (!importFile) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());

        let imported = 0;
        // Expected: name, email, phone, region, states, branches
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          const officer = {
            name: values[headers.indexOf('name')] || '',
            email: values[headers.indexOf('email')] || '',
            phone: values[headers.indexOf('phone')] || '',
            region: values[headers.indexOf('region')] || '',
            states: (values[headers.indexOf('states')] || '').split(';').map(s => s.trim()).filter(Boolean),
            branches: (values[headers.indexOf('branches')] || '').split(';').map(b => b.trim()).filter(Boolean)
          };

          if (officer.name && officer.email) {
            const token = localStorage.getItem('token');
            const response = await fetch(`${backendUrl}/api/admin/regional-officers`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Local-Dev': 'true',
                ...(token && { 'Authorization': `Bearer ${token}` })
              },
              body: JSON.stringify(officer)
            });
            if (response.ok) imported++;
          }
        }

        alert(`✅ Imported ${imported} officers`);
        setShowImportModal(false);
        setImportFile(null);
        loadRegionalOfficers();
      } catch (err) {
        alert(`❌ Import failed: ${err.message}`);
      }
    };
    reader.readAsText(importFile);
  };

  const getSortedOfficers = () => {
    const sorted = [...regionalOfficers];
    sorted.sort((a, b) => {
      if (sortBy === 'name') {
        return (a.name || '').localeCompare(b.name || '');
      } else if (sortBy === 'region') {
        return (a.region || '').localeCompare(b.region || '');
      } else if (sortBy === 'states') {
        return (a.states?.[0] || '').localeCompare(b.states?.[0] || '');
      }
      return 0;
    });
    return sorted;
  };

  // Build hierarchy: RSLs with their ASMs nested (with collapsible support)
  const getHierarchicalOfficers = () => {
    const rvps = regionalOfficers.filter(o => o.role === 'regional_sales_leader' || !o.role);
    const rds = regionalOfficers.filter(o => o.role === 'area_sales_manager');
    const asms = regionalOfficers.filter(o => o.role === 'asm');

    const sortedRvps = [...rvps].sort((a, b) => {
      if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'region') return (a.region || '').localeCompare(b.region || '');
      if (sortBy === 'states') return (a.states?.[0] || '').localeCompare(b.states?.[0] || '');
      return 0;
    });

    const hierarchy = [];
    sortedRvps.forEach(rvp => {
      const rvpId = rvp.id;

      const childRds = rds
        .filter(rd => rd.reports_to === rvpId)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

      hierarchy.push({
        ...rvp,
        isRSL: true,
        hasChildren: childRds.length > 0,
        childrenCount: childRds.length
      });

      const isExpanded = expandedRSLs[rvpId] !== false;
      if (isExpanded || childRds.length === 0) {
        childRds.forEach(rd => {
          const childAsms = asms
            .filter(a => a.reports_to === (rd.id || rd._id))
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

          hierarchy.push({ ...rd, isASM: true, parentRSL: rvp, hasChildren: childAsms.length > 0, childrenCount: childAsms.length });

          childAsms.forEach(asm => {
            hierarchy.push({ ...asm, isTier3ASM: true, parentRD: rd, parentRVP: rvp });
          });
        });
      }
    });

    return hierarchy;
  };

  const toggleRSL = (rslId) => {
    setExpandedRSLs(prev => ({
      ...prev,
      [rslId]: prev[rslId] === false ? true : false
    }));
  };

  // Calculate rolled-up team counts AND total points
  // 🔧 FIX: Also include officer's PERSONAL points (not just team's)
  const getRolledUpTeamStats = useMemo(() => {
    const stats = {};
    const asms = regionalOfficers.filter(o => o.role === 'area_sales_manager');

    // First calculate ASM counts and points from their assigned users
    (usersStats || []).forEach(u => {
      const asmId = u.area_sales_manager_id;
      if (asmId) {
        if (!stats[asmId]) {
          stats[asmId] = { count: 0, points: 0, personal: 0 };
        }
        stats[asmId].count += 1;
        stats[asmId].points += (u.total_points || 0);
      }
    });

    // 🔧 FIX: Add each ASM's own personal points
    asms.forEach(asm => {
      const asmEmail = (asm.email || '').toLowerCase();
      const asmUser = (usersStats || []).find(u => (u.email || '').toLowerCase() === asmEmail);
      const personalPoints = asmUser?.total_points || 0;
      
      if (!stats[asm.id]) {
        stats[asm.id] = { count: 0, points: 0, personal: 0 };
      }
      stats[asm.id].personal = personalPoints;
      // Add personal points to the total points display
      stats[asm.id].points += personalPoints;
    });

    // Then calculate RSL counts and points (direct + from ASMs)
    regionalOfficers.forEach(rsl => {
      if (rsl.role === 'regional_sales_leader' || !rsl.role) {
        const rslId = rsl.id;
        const rslEmail = (rsl.email || '').toLowerCase();

        // RSL's own personal points
        const rslUser = (usersStats || []).find(u => (u.email || '').toLowerCase() === rslEmail);
        const rslPersonalPoints = rslUser?.total_points || 0;

        // Direct users assigned to RSL
        const directUsers = (usersStats || []).filter(u =>
          u.regional_sales_leader_id === rslId && !u.area_sales_manager_id
        );
        const directCount = directUsers.length;
        const directPoints = directUsers.reduce((sum, u) => sum + (u.total_points || 0), 0);

        // Users assigned to ASMs under this RSL
        const childAsms = asms.filter(asm => asm.reports_to === rslId);
        const asmCount = childAsms.reduce((sum, asm) => sum + (stats[asm.id]?.count || 0), 0);
        const asmPoints = childAsms.reduce((sum, asm) => sum + (stats[asm.id]?.points || 0), 0);

        stats[rslId] = {
          count: directCount + asmCount,
          points: directPoints + asmPoints + rslPersonalPoints,  // Include RSL's personal points
          personal: rslPersonalPoints
        };
      }
    });

    return stats;
  }, [regionalOfficers, usersStats]);

  const getNavOffset = () => {
    switch (navMode) {
      case 'peek': return 'ml-4 w-[calc(100%-1rem)]';
      case 'collapsed': return 'ml-20 w-[calc(100%-5rem)]';
      default: return 'ml-64 w-[calc(100%-16rem)]';
    }
  };

  const [activeActionMenu, setActiveActionMenu] = useState(null); // ID of officer with open action menu

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveActionMenu(null);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className={`min-h-screen bg-gradient-to-br from-[#00314F] via-[#003d5c] to-[#00314F] text-white ${getNavOffset()} overflow-x-hidden`}>
      <AdminNav />
      <div className="pt-6 px-4 max-w-full">
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1">Regional Vice Presidents</h1>
          <p className="text-gray-400 text-xs sm:text-sm">Configure who receives leads from each region</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-yellow-500/20 border border-yellow-500 rounded-lg p-4">
            <p className="text-yellow-400">⚠️ {error}</p>
            <p className="text-yellow-300 text-sm mt-2">Using placeholder data for now. Backend endpoint needs to be created.</p>
          </div>
        )}

        {/* Controls - Mobile Optimized */}
        <div className="mb-6 flex flex-col lg:flex-row gap-3 items-stretch lg:items-center justify-between">
          {/* Primary Action - Full width on mobile */}
          <button
            onClick={() => {
              setShowAddForm(!showAddForm);
              if (showAddForm) {
                setEditingOfficer(null);
                setNewOfficer({ name: '', email: '', phone: '', region: '', branches: [], states: [], role: 'regional_sales_leader', reports_to: null });
              }
            }}
            className="bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/80 px-4 py-3 sm:py-2.5 rounded-lg font-semibold transition-colors text-sm flex items-center justify-center gap-2 shadow-lg sm:shadow-none"
          >
            {showAddForm ? <><span>✖</span> Cancel</> : <><span>➕</span> Add Regional Vice President</>}
          </button>

          {/* Secondary Actions & Sort - Grid on mobile, Flex on desktop */}
          <div className="grid grid-cols-2 sm:flex gap-2 items-center">
            <button
              onClick={() => setShowImportModal(true)}
              className="col-span-1 bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 hover:text-white px-3 py-2.5 rounded-lg transition-colors text-xs sm:text-sm text-center"
              title="Import CSV"
            >
              📥 Import
            </button>

            <button
              onClick={() => {
                loadUsersStats();
                loadRegionalOfficers();
              }}
              disabled={loadingUsers || loading}
              className="col-span-1 bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 hover:text-white px-3 py-2.5 rounded-lg transition-colors disabled:opacity-50 text-xs sm:text-sm text-center"
              title="Refresh team counts and points"
            >
              🔄 Refresh
            </button>

            {/* Sort Dropdown - Full width on mobile row 2 */}
            <div className="col-span-2 sm:col-span-0 flex items-center gap-2">
              <label className="text-white/60 text-sm whitespace-nowrap">Sort by:</label>
              <CustomDropdown
                value={sortBy}
                onChange={(val) => setSortBy(val)}
                minWidth="110px"
                className="flex-1 sm:flex-initial"
                options={[
                  { value: 'name', label: 'Name' },
                  { value: 'region', label: 'Region' },
                  { value: 'states', label: 'State' }
                ]}
              />
            </div>
          </div>
        </div>

        {/* Add Officer Form */}
        {showAddForm && (
          <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6 mb-8">
            <h2 className="text-2xl font-bold mb-4">{editingOfficer ? 'Edit Officer' : 'Add New Officer'}</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-white/60 mb-2">Name *</label>
                <input
                  type="text"
                  value={newOfficer.name}
                  onChange={(e) => setNewOfficer({ ...newOfficer, name: e.target.value })}
                  placeholder="John Smith"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[var(--pbfs-blue)]"
                />
              </div>

              <div>
                <label className="block text-sm text-white/60 mb-2">Email *</label>
                <input
                  type="text"
                  value={newOfficer.email}
                  onChange={(e) => setNewOfficer({ ...newOfficer, email: e.target.value.toLowerCase() })}
                  placeholder="john.smith@pyebarkerfs.com"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[var(--pbfs-blue)]"
                  autoComplete="off"
                />
                <div className="text-xs text-white/40 mt-1">Must end with @pyebarkerfs.com</div>
              </div>

              <div>
                <label className="block text-sm text-white/60 mb-2">Role *</label>
                <CustomDropdown
                  value={newOfficer.role}
                  onChange={(val) => setNewOfficer({ ...newOfficer, role: val, reports_to: val === 'regional_sales_leader' ? null : '' })}
                  className="w-full"
                  minWidth="100%"
                  options={[
                    ...ROLE_OPTIONS.map(o => ({ value: o.value, label: o.label }))
                  ]}
                />
                <div className="text-xs text-white/40 mt-1">
                  {newOfficer.role === 'regional_sales_leader' ? 'Top-level regional leader' :
                   newOfficer.role === 'area_sales_manager' ? 'Reports to a Regional Vice President' :
                   newOfficer.role === 'asm' ? 'Reports to a Regional Director' : ''}
                </div>
              </div>

              {newOfficer.role === 'area_sales_manager' && (
                <div>
                  <label className="block text-sm text-white/60 mb-2">Reports To (RVP) *</label>
                  <CustomDropdown
                    value={newOfficer.reports_to || ''}
                    onChange={(val) => setNewOfficer({ ...newOfficer, reports_to: val || null })}
                    className="w-full"
                    minWidth="100%"
                    placeholder="Select Regional Vice President..."
                    options={[
                      { value: '', label: 'Select Regional Vice President...' },
                      ...regionalOfficers
                        .filter(o => o.role === 'regional_sales_leader' || !o.role)
                        .map(rsl => ({
                          value: rsl.id,
                          label: rsl.name
                        }))
                    ]}
                  />
                  <div className="text-xs text-white/40 mt-1">Which RVP does this RD report to?</div>
                </div>
              )}

              {newOfficer.role === 'asm' && (
                <div>
                  <label className="block text-sm text-white/60 mb-2">Reports To (RD) *</label>
                  <CustomDropdown
                    value={newOfficer.reports_to || ''}
                    onChange={(val) => setNewOfficer({ ...newOfficer, reports_to: val || null })}
                    className="w-full"
                    minWidth="100%"
                    placeholder="Select Regional Director..."
                    options={[
                      { value: '', label: 'Select Regional Director...' },
                      ...regionalOfficers
                        .filter(o => o.role === 'area_sales_manager')
                        .map(rd => ({
                          value: rd.id,
                          label: rd.name
                        }))
                    ]}
                  />
                  <div className="text-xs text-white/40 mt-1">Which RD does this ASM report to?</div>
                </div>
              )}

              <div>
                <label className="block text-sm text-white/60 mb-2">Pye-Barker Region *</label>
                <CustomDropdown
                  value={newOfficer.region}
                  onChange={(val) => setNewOfficer({ ...newOfficer, region: val })}
                  className="w-full"
                  minWidth="100%"
                  placeholder="Select a region..."
                  options={[
                    { value: '', label: 'Select a region...' },
                    { value: 'Mid-Atlantic', label: 'Mid-Atlantic' },
                    { value: 'Midwest', label: 'Midwest' },
                    { value: 'Northeast', label: 'Northeast' },
                    { value: 'South', label: 'South' },
                    { value: 'Southeast', label: 'Southeast' },
                    { value: 'Southwest', label: 'Southwest' },
                    { value: 'West', label: 'West' }
                  ]}
                />
              </div>
            </div>

            <div className="mt-4 flex space-x-3">
              <button
                onClick={handleAddOfficer}
                disabled={saving}
                className="bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/80 disabled:bg-white/10 px-6 py-2 rounded-lg font-semibold transition-colors"
              >
                {saving ? 'Saving...' : 'Save Officer'}
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                className="bg-white/10 hover:bg-white/20 px-6 py-2 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Officers List */}
        <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold">Current Regional Vice Presidents</h2>
              <button
                onClick={() => setShowOfficersList(!showOfficersList)}
                className="bg-white/10 hover:bg-white/20 px-3 py-1 rounded text-sm transition-colors flex items-center gap-2"
              >
                {showOfficersList ? '▼ Hide' : '▶ Show'} ({regionalOfficers.length})
              </button>
            </div>
          </div>

          {showOfficersList && (
            loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--pbfs-green)] mx-auto"></div>
                <p className="text-white/60 mt-4">Loading...</p>
              </div>
            ) : regionalOfficers.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-white/40">No regional officers configured yet</p>
                <p className="text-white/30 text-sm mt-2">Click "Add Regional Vice President" to get started</p>
              </div>
            ) : (
              <div className="space-y-4">
                {getHierarchicalOfficers().map((officer) => (
                  <div
                    key={officer.id}
                    className={`bg-white/5 border border-white/10 rounded-lg p-4 hover:border-white/20 transition-colors ${officer.isASM ? 'ml-0 sm:ml-8 border-l-4 border-l-[var(--pbfs-blue)]' : ''} ${officer.isTier3ASM ? 'ml-0 sm:ml-16 border-l-4 border-l-[var(--pbfs-gray)]' : ''
                      }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0 pr-2">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          {(officer.isASM || officer.isTier3ASM) && <span className="text-[var(--pbfs-blue)]">↳</span>}

                          {/* Expand/Collapse Button for RSLs with children */}
                          {officer.isRSL && officer.hasChildren && (
                            <button
                              onClick={() => toggleRSL(officer.id)}
                              className="text-white/60 hover:text-white transition-colors flex-shrink-0"
                              title={expandedRSLs[officer.id] === false ? 'Expand' : 'Collapse'}
                            >
                              {expandedRSLs[officer.id] === false ? '▶' : '▼'}
                            </button>
                          )}

                          <h3 className="text-lg sm:text-xl font-semibold text-white truncate max-w-[200px] sm:max-w-none">{officer.name}</h3>

                          {officer.is_default && (
                            <span className="bg-[var(--pbfs-green)]/20 text-[var(--pbfs-green)] text-[10px] sm:text-xs px-2 py-0.5 rounded">
                              Default
                            </span>
                          )}

                          {officer.isRSL && (
                            <span className="bg-[var(--pbfs-green)]/20 text-[var(--pbfs-green)] text-[10px] sm:text-xs px-2 py-0.5 rounded font-bold">
                              RVP
                            </span>
                          )}

                          {officer.isASM && (
                            <span className="bg-[var(--pbfs-blue)]/20 text-[var(--pbfs-blue)] text-[10px] sm:text-xs px-2 py-0.5 rounded font-bold">
                              RD
                            </span>
                          )}

                          {officer.isTier3ASM && (
                            <span className="bg-[var(--pbfs-gray)]/20 text-[var(--pbfs-gray)] text-[10px] sm:text-xs px-2 py-0.5 rounded font-bold">
                              ASM
                            </span>
                          )}

                          {!officer.is_default && (
                            <div className="flex items-center gap-2 mt-1 sm:mt-0">
                              <span className="bg-white/10 border border-white/10 text-white/70 text-[10px] sm:text-xs px-2 py-0.5 rounded whitespace-nowrap">
                                Team: {getRolledUpTeamStats[officer.id]?.count || 0}
                              </span>
                              <span className="bg-yellow-500/20 border border-yellow-500/40 text-yellow-400 text-[10px] sm:text-xs px-2 py-0.5 rounded font-semibold whitespace-nowrap">
                                {(getRolledUpTeamStats[officer.id]?.points || 0).toLocaleString()} pts
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="space-y-1 text-sm text-white/70">
                          <div className="flex items-center space-x-2 truncate">
                            <span className="opacity-50">📧</span>
                            <span className="truncate">{officer.email}</span>
                          </div>

                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            <div className="flex items-center space-x-2">
                              <span className="opacity-50">📍</span>
                              <span>{officer.region}</span>
                            </div>
                            {officer.phone && (
                              <div className="flex items-center space-x-2">
                                <span className="opacity-50">📞</span>
                                <span>{officer.phone}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {!officer.is_default && (
                        <div className="relative">
                          {/* Mobile Dropdown / Desktop Buttons */}

                          {/* Desktop View (Hidden on mobile) */}
                          <div className="hidden lg:flex flex-wrap gap-2 items-start justify-end">
                            <button
                              onClick={async () => await openTeamModal(officer)}
                              className="bg-[var(--pbfs-green)]/20 hover:bg-[var(--pbfs-green)]/30 text-[var(--pbfs-green)] px-3 py-1.5 rounded text-xs transition-colors whitespace-nowrap"
                            >
                              Team
                            </button>
                            <button
                              onClick={() => { setHierarchyOfficer(officer); setShowTeamHierarchyModal(true); }}
                              className="bg-[var(--pbfs-blue)]/20 hover:bg-[var(--pbfs-blue)]/30 text-[var(--pbfs-blue)] px-3 py-1.5 rounded text-xs transition-colors whitespace-nowrap"
                            >
                              Activity
                            </button>
                            <button
                              onClick={() => {
                                setEditingOfficer(officer);
                                setNewOfficer({
                                  name: officer.name,
                                  email: officer.email,
                                  phone: officer.phone || '',
                                  region: officer.region,
                                  branches: officer.branches || [],
                                  states: officer.states || [],
                                  role: officer.role || 'regional_sales_leader',
                                  reports_to: officer.reports_to || null
                                });
                                setShowAddForm(true);
                              }}
                              className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 px-3 py-1.5 rounded text-xs transition-colors whitespace-nowrap"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteOfficer(officer.id)}
                              className="bg-red-500/20 hover:bg-red-500/30 text-red-400 px-3 py-1.5 rounded text-xs transition-colors whitespace-nowrap"
                            >
                              Remove
                            </button>
                          </div>

                          {/* Mobile Menu Button (Visible < lg) */}
                          <div className="lg:hidden">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveActionMenu(activeActionMenu === officer.id ? null : officer.id);
                              }}
                              className="p-2 hover:bg-white/10 rounded-lg text-white/80 transition-colors"
                            >
                              ⚙️
                            </button>

                            {/* Dropdown Menu */}
                            {activeActionMenu === officer.id && (
                              <div className="absolute right-0 top-full mt-2 w-48 bg-gray-900 border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden animate-fade-in-up">
                                <div className="py-1">
                                  <button
                                    onClick={() => openTeamModal(officer)}
                                    className="w-full text-left px-4 py-3 hover:bg-white/5 text-sm text-white flex items-center gap-2"
                                  >
                                    <span>👥</span> Manage Team
                                  </button>
                                  <button
                                    onClick={() => { setHierarchyOfficer(officer); setShowTeamHierarchyModal(true); }}
                                    className="w-full text-left px-4 py-3 hover:bg-white/5 text-sm text-white flex items-center gap-2"
                                  >
                                    <span>📊</span> View Activity
                                  </button>
                                  <div className="h-px bg-white/10 my-1"></div>
                                  <button
                                    onClick={() => {
                                      setEditingOfficer(officer);
                                      setNewOfficer({ /* same setup */
                                        name: officer.name,
                                        email: officer.email,
                                        phone: officer.phone || '',
                                        region: officer.region,
                                        branches: officer.branches || [],
                                        states: officer.states || [],
                                        role: officer.role || 'regional_sales_leader',
                                        reports_to: officer.reports_to || null
                                      });
                                      setShowAddForm(true);
                                      setActiveActionMenu(null);
                                    }}
                                    className="w-full text-left px-4 py-3 hover:bg-white/5 text-sm text-blue-400 flex items-center gap-2"
                                  >
                                    <span>✏️</span> Edit Details
                                  </button>
                                  <button
                                    onClick={() => handleDeleteOfficer(officer.id)}
                                    className="w-full text-left px-4 py-3 hover:bg-white/5 text-sm text-red-400 flex items-center gap-2"
                                  >
                                    <span>🗑️</span> Remove
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>

                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>

      </div>

      {/* Team Modal */}
      {teamModalOfficer && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100000] flex items-center justify-center p-4 pt-20 pb-6" onClick={closeTeamModal}>
          <div
            className="bg-[var(--pbfs-navy)] border border-white/10 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-white/10 flex items-start justify-between gap-3">
              <div>
                <div className="text-[var(--pbfs-green)] font-bold text-lg tracking-wide">
                  {teamModalOfficer.name}'s Team
                </div>
                <div className="text-white/60 text-sm">
                  Assign users to this officer for routing + reporting.
                </div>
              </div>
              <button
                onClick={closeTeamModal}
                className="text-white/60 hover:text-white text-2xl leading-none"
                title="Close"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="flex flex-col md:flex-row gap-3 md:items-center">
                <input
                  value={teamSearch}
                  onChange={(e) => setTeamSearch(e.target.value)}
                  placeholder="Search by name or email…"
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white placeholder:text-white/30 focus:outline-none focus:border-[var(--pbfs-blue)]"
                />
                <button
                  onClick={loadUsersStats}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg text-white/80 text-sm transition-colors"
                  disabled={loadingUsers}
                >
                  {loadingUsers ? 'Refreshing…' : 'Refresh'}
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Assigned - Hierarchical View */}
                <div className="bg-black/20 border border-white/10 rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                    <div className="text-white/80 font-semibold text-sm">Assigned</div>
                    <div className="text-white/40 text-xs">{modalLists.assigned.length}</div>
                  </div>
                  <div className="max-h-[52vh] overflow-y-auto">
                    {modalLists.hierarchy.length === 0 ? (
                      <div className="p-4 text-white/40 text-sm">No users assigned yet.</div>
                    ) : (
                      modalLists.hierarchy.map((item, idx) => {
                        // ASM with nested users
                        if (item.type === 'asm') {
                          return (
                            <div key={item.officer.id || idx}>
                              {/* ASM Header */}
                              <div className="px-4 py-3 bg-[var(--pbfs-blue)]/20 border-b border-white/10 flex items-center justify-between gap-3">
                                <div className="min-w-0 flex items-center gap-2">
                                  <span className="text-[var(--pbfs-blue)]">👔</span>
                                  <div>
                                    <div className="text-white/90 text-sm font-semibold truncate">
                                      {item.officer.name}
                                      <span className="text-white/40 text-xs ml-2">RD</span>
                                    </div>
                                    <div className="text-white/50 text-xs truncate">{item.officer.email}</div>
                                    <div className="text-white/40 text-[11px] mt-1">
                                      {item.users.length} user{item.users.length !== 1 ? 's' : ''} • {item.totalPoints} pts
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {/* Users under this ASM */}
                              {item.users.map(u => (
                                <div key={u.email} className="px-4 py-3 border-b border-white/5 flex items-center justify-between gap-3 pl-8">
                                  <div className="min-w-0 flex items-start gap-2">
                                    <span className="text-white/30 text-sm">└</span>
                                    <div>
                                      <div className="text-white/90 text-sm font-semibold truncate">{u.name || 'User'}</div>
                                      <div className="text-white/50 text-xs truncate">{u.email}</div>
                                      <div className="text-white/40 text-[11px] mt-1">
                                        {u.total_points ?? 0} pts • {u.total_scans ?? u.total_submissions ?? u.stats?.total_inspections ?? 0} scans
                                      </div>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => assignUserToLeader(u.email, null)}
                                    disabled={assigningEmail === (u.email || '').toLowerCase()}
                                    className="flex-shrink-0 px-3 py-2 bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg text-white/80 text-xs transition-colors disabled:opacity-60"
                                    title="Remove from this team"
                                  >
                                    {assigningEmail === (u.email || '').toLowerCase() ? 'Saving…' : 'Remove'}
                                  </button>
                                </div>
                              ))}
                            </div>
                          );
                        }
                        
                        // Direct user (no ASM)
                        const u = item.user;
                        return (
                          <div key={u.email} className="px-4 py-3 border-b border-white/5 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-white/90 text-sm font-semibold truncate">{u.name || 'User'}</div>
                              <div className="text-white/50 text-xs truncate">{u.email}</div>
                              <div className="text-white/40 text-[11px] mt-1">
                                {u.total_points ?? 0} pts • {u.total_scans ?? u.total_submissions ?? u.stats?.total_inspections ?? 0} scans
                              </div>
                            </div>
                            <button
                              onClick={() => assignUserToLeader(u.email, null)}
                              disabled={assigningEmail === (u.email || '').toLowerCase()}
                              className="flex-shrink-0 px-3 py-2 bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg text-white/80 text-xs transition-colors disabled:opacity-60"
                              title="Remove from this team"
                            >
                              {assigningEmail === (u.email || '').toLowerCase() ? 'Saving…' : 'Remove'}
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Unassigned */}
                <div className="bg-black/20 border border-white/10 rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                    <div className="text-white/80 font-semibold text-sm">Unassigned</div>
                    <div className="text-white/40 text-xs">{modalLists.unassigned.length}</div>
                  </div>
                  <div className="max-h-[52vh] overflow-y-auto">
                    {modalLists.unassigned.length === 0 ? (
                      <div className="p-4 text-white/40 text-sm">No unassigned users found (or emails not captured yet).</div>
                    ) : (
                      modalLists.unassigned.map((u) => {
                        const isRSL = (teamModalOfficer?.role || 'regional_sales_leader') === 'regional_sales_leader';
                        const hasASMs = modalLists.asmsUnderRSL?.length > 0;
                        const dropdownOpen = assignDropdownEmail === u.email;
                        
                        return (
                          <div key={u.email} className="px-4 py-3 border-b border-white/5 flex items-center justify-between gap-3 relative">
                            <div className="min-w-0">
                              <div className="text-white/90 text-sm font-semibold truncate">{u.name || 'User'}</div>
                              <div className="text-white/50 text-xs truncate">{u.email}</div>
                              <div className="text-white/40 text-[11px] mt-1">
                                {u.total_points ?? 0} pts • {u.total_scans ?? u.total_submissions ?? u.stats?.total_inspections ?? 0} scans
                              </div>
                            </div>
                            
                            {/* Add button - with dropdown for RSL if there are ASMs */}
                            {isRSL && hasASMs ? (
                              <div className="relative">
                                <button
                                  onClick={() => setAssignDropdownEmail(dropdownOpen ? null : u.email)}
                                  disabled={assigningEmail === (u.email || '').toLowerCase()}
                                  className="flex-shrink-0 px-3 py-2 bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/90 rounded-lg text-white text-xs font-semibold transition-colors disabled:opacity-60 flex items-center gap-1"
                                  title="Add to team"
                                >
                                  {assigningEmail === (u.email || '').toLowerCase() ? 'Saving…' : (
                                    <>Add <span className="text-[10px]">▼</span></>
                                  )}
                                </button>
                                
                                {/* Dropdown menu */}
                                {dropdownOpen && (
                                  <div className="absolute right-0 top-full mt-1 bg-[var(--pbfs-navy)] border border-white/20 rounded-lg shadow-xl z-50 min-w-[200px] overflow-hidden">
                                    {/* Add directly to RSL */}
                                    <button
                                      onClick={() => {
                                        setAssignDropdownEmail(null);
                                        assignUserToLeader(u.email, teamModalOfficer);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm text-white/90 hover:bg-white/10 transition-colors border-b border-white/10"
                                    >
                                      👤 Direct to {teamModalOfficer?.name?.split(' ')[0]}
                                    </button>
                                    
                                    {/* Add to each ASM */}
                                    {modalLists.asmsUnderRSL.map(asm => (
                                      <button
                                        key={asm.id}
                                        onClick={() => {
                                          setAssignDropdownEmail(null);
                                          assignUserToLeader(u.email, asm);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm text-white/90 hover:bg-white/10 transition-colors flex items-center gap-2"
                                      >
                                        <span className="text-white/30">└</span>
                                        <span className="text-[var(--pbfs-blue)]">👔</span>
                                        {asm.name}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <button
                                onClick={() => assignUserToLeader(u.email, teamModalOfficer)}
                                disabled={assigningEmail === (u.email || '').toLowerCase()}
                                className="flex-shrink-0 px-3 py-2 bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/90 rounded-lg text-white text-xs font-semibold transition-colors disabled:opacity-60"
                                title="Add to this team"
                              >
                                {assigningEmail === (u.email || '').toLowerCase() ? 'Saving…' : 'Add'}
                              </button>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              <div className="text-white/40 text-xs">
                Tip: users without captured emails won’t appear here yet. Once email capture fills in, they’ll show up automatically.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100000] flex items-center justify-center p-4 pt-20 pb-6">
          <div className="bg-[var(--pbfs-navy)] border border-white/20 rounded-xl p-6 max-w-2xl w-full">
            <h2 className="text-2xl font-bold mb-4">📥 Import Regional Vice Presidents (CSV)</h2>

            <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
              <p className="text-white/80 text-sm mb-2">CSV Format Expected:</p>
              <code className="text-[var(--pbfs-green)] text-xs block bg-black/30 p-2 rounded">
                name,email,phone,region,states,branches<br />
                John Smith,john.smith@pyebarkerfs.com,(555) 123-4567,Southeast,GA;FL;SC,Atlanta;Miami
              </code>
              <p className="text-white/50 text-xs mt-2">Use semicolons (;) to separate multiple states/branches</p>
            </div>

            <input
              type="file"
              accept=".csv"
              onChange={(e) => setImportFile(e.target.files[0])}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white mb-4"
            />

            <div className="flex space-x-3">
              <button
                onClick={handleImportCSV}
                disabled={!importFile}
                className="bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/80 disabled:bg-white/10 px-6 py-2 rounded-lg font-semibold transition-colors"
              >
                Import
              </button>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                }}
                className="bg-white/10 hover:bg-white/20 px-6 py-2 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🌳 Team Hierarchy Modal - View team submissions and activity */}
      <TeamHierarchyModal
        isOpen={showTeamHierarchyModal}
        onClose={() => {
          setShowTeamHierarchyModal(false);
          setHierarchyOfficer(null);
        }}
        userEmail={hierarchyOfficer?.email}
        backendUrl={backendUrl}
        days={30}
      />
    </div>
  );
};

export default RegionalOfficersManagement;

